import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { getPortalInvoices } from '../../services/portalService';
import Badge from '../../components/common/Badge';
import Spinner from '../../components/common/Spinner';
import EmptyState from '../../components/common/EmptyState';

const badgeVariant = (status) => {
  if (status === 'paid') return 'success';
  if (status === 'overdue') return 'error';
  if (status === 'cancelled') return 'inactive';
  return 'default';
};

export default function PortalInvoices() {
  const { data: invoices, isLoading } = useQuery({
    queryKey: ['portal', 'invoices'],
    queryFn: getPortalInvoices,
  });

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  const totalOwed = invoices
    ?.filter((inv) => !['paid', 'cancelled'].includes(inv.status))
    .reduce((sum, inv) => sum + (parseFloat(inv.total) - parseFloat(inv.amountPaid)), 0) ?? 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Invoices</h1>

      {totalOwed > 0 && (
        <div className="rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 px-5 py-4">
          <p className="text-sm font-medium text-danger-800 dark:text-danger-300">
            Outstanding balance: <span className="text-lg font-bold">${totalOwed.toFixed(2)}</span>
          </p>
          <p className="text-xs text-danger-700 dark:text-danger-400 mt-0.5">
            Please contact your practitioner to arrange payment.
          </p>
        </div>
      )}

      <div className="card">
        {!invoices?.length ? (
          <EmptyState title="No invoices" description="Your invoices will appear here." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-neutral-600 dark:text-neutral-400 uppercase tracking-wide border-b border-neutral-200 dark:border-neutral-700">
                <th className="pb-3 pr-4">Invoice</th>
                <th className="pb-3 pr-4">Date</th>
                <th className="pb-3 pr-4">Amount</th>
                <th className="pb-3 pr-4">Balance</th>
                <th className="pb-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {invoices.map((inv) => {
                const balance = parseFloat(inv.total) - parseFloat(inv.amountPaid);
                return (
                  <tr key={inv.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/40 transition-colors">
                    <td className="py-3 pr-4">
                      <Link
                        to={`/portal/invoices/${inv.id}`}
                        className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                      >
                        {inv.invoiceNumber}
                      </Link>
                    </td>
                    <td className="py-3 pr-4 text-neutral-700 dark:text-neutral-300">
                      {format(new Date(inv.invoiceDate), 'MMM d, yyyy')}
                    </td>
                    <td className="py-3 pr-4 text-neutral-900 dark:text-neutral-100">${parseFloat(inv.total).toFixed(2)}</td>
                    <td className={`py-3 pr-4 font-medium ${balance > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-900 dark:text-neutral-100'}`}>
                      ${balance.toFixed(2)}
                    </td>
                    <td className="py-3">
                      <Badge label={inv.status} variant={badgeVariant(inv.status)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
